import { FC, useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import PendingProposalItem from './PendingProposalItem'
import RequestLeaseReviewContent from './RequestLeaseReviewContent'
import { ProposalTabs, ProposalTabKey } from './ProposalTabs'
import { ProposalQuoteOption, ProposalReviewView } from '@/types'
import { Navbar } from '../../../layout/Navbar'
import { AccountLayout } from '../../../layout/AccountLayout'
import { authService } from '../../../../services/authService'
import apiClient from '../../../../services/api'
import { useFlowSelectionLogic } from '../../../../hooks/useFlowSelectionLogic'
import GuarantorInviteModal from './GuarantorInviteModal'
import { isCreditApplicationTerminal } from '../../../../utils/creditApplicationStatus'
import type { CreditApplicationSummary } from '../../../../services/authService'

interface PendingProposalsContentProps {
  proposals: ProposalQuoteOption[]
  setSelectedProposal?: (proposal: ProposalQuoteOption) => void
  setView?: (view: ProposalReviewView) => void
  onRefresh?: () => void
  // Refresco silencioso (no pasa por el loader de página completa) — se usa
  // al regresar a la tab "Activas" para no perder el estado de este
  // componente (activeTab, etc.) mientras se traen datos frescos.
  onRefreshActivas?: () => void
  // Trae las cotizaciones vencidas bajo demanda (soloExpiradas=true en el
  // GET) — se llama cada vez que el usuario abre esa tab.
  onLoadVencidas?: () => Promise<ProposalQuoteOption[]>
}

const PendingProposalsContent: FC<PendingProposalsContentProps> = ({
  proposals,
  onRefresh,
  onRefreshActivas,
  onLoadVencidas,
}) => {
  const [creditAppId, setCreditAppId] = useState<string | null>(null)
  const [isLoadingCreditApp, setIsLoadingCreditApp] = useState(true)
  // ids de cotización (ProposalQuoteOption.id === creditApp.cotizacion.id) que
  // ya generaron alguna credit application en el pasado, sin importar su
  // estatus — no pueden volver a seleccionarse para una solicitud nueva.
  const [usedCotizacionIds, setUsedCotizacionIds] = useState<Set<string>>(
    new Set()
  )
  // true si la credit application más reciente (creditApps[0], el backend
  // garantiza ese orden) todavía no llega a un estatus terminal.
  const [hasActiveCreditApp, setHasActiveCreditApp] = useState(false)
  // id de la cotización con una solicitud en curso (creditApps[0], mientras
  // no sea terminal) y su progressUrl — se llenan para cualquier solicitud
  // (1ra o posterior). Se usan para mostrar la tarjeta "Continuar" en vez
  // del flujo normal de selección (comportamiento híbrido, ver plan Parte
  // B.4).
  const [inProgressCotizacionId, setInProgressCotizacionId] = useState<
    string | null
  >(null)
  const [inProgressUrl, setInProgressUrl] = useState<string | null>(null)
  // true mientras el usuario siga en su 1ra solicitud (ver
  // authService.isFirstCreditApplication) — determina si "Solicitar" crea
  // la credit application de inmediato (comportamiento actual) o primero
  // muestra la pantalla "Revisa los detalles" (reviewingProposal, abajo).
  const [isFirstRequest, setIsFirstRequest] = useState(true)
  // Cotización que el usuario está revisando antes de confirmar la
  // solicitud (pantalla "Revisa los detalles"), solo aplica a partir de la
  // 2da solicitud. null = se muestra el listado normal.
  const [reviewingProposal, setReviewingProposal] =
    useState<ProposalQuoteOption | null>(null)
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)
  // id de la cotización cuya tarjeta disparó handleRequest — para mostrarle
  // el spinner de "Solicitando…" solo a ella y no a las demás mientras
  // isSubmittingRequest está en true.
  const [requestingProposalId, setRequestingProposalId] = useState<
    string | null
  >(null)
  const [activeTab, setActiveTab] = useState<ProposalTabKey>('activas')
  const [vencidasProposals, setVencidasProposals] = useState<
    ProposalQuoteOption[]
  >([])
  const [isLoadingVencidas, setIsLoadingVencidas] = useState(false)
  const isProcessingRef = useRef(false)
  // El primer disparo del efecto de abajo corresponde al montaje (tab
  // "activas" inicial), cuyos datos ya llegan frescos desde
  // PendingProposalsRoute — se salta ahí para no duplicar ese fetch.
  const isFirstTabEffect = useRef(true)
  const [isGuarantorModalOpen, setIsGuarantorModalOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  // La cotización en curso deja de mostrar "Continuar" si su progressUrl
  // guardado apunta a esta misma pantalla (puede pasar justo al crear la
  // solicitud, antes de que triggerFlowSelection lo actualice al primer
  // paso real) — navegar ahí no llevaría a ningún lado nuevo.
  const isProgressUrlHere =
    !!inProgressUrl && inProgressUrl === location.pathname

  // Trae el historial completo de credit applications y deriva de ahí todo
  // lo que este componente necesita: las cotizaciones ya usadas y el
  // estado/id de la solicitud más reciente. Centraliza lo que antes eran 3
  // fetches casi idénticos (montaje, éxito de aval, catch de handleRequest).
  const loadCreditAppsState = async (): Promise<{
    creditApps: CreditApplicationSummary[]
    current: CreditApplicationSummary | null
  }> => {
    const creditApps = await authService.getAllCreditApplications()

    const usedIds = new Set<string>(
      creditApps.map(ca => ca.cotizacion?.id).filter((id): id is string => !!id)
    )
    setUsedCotizacionIds(usedIds)

    // creditApps[0] siempre es la más reciente.
    const current = creditApps[0] ?? null
    const isCurrentInProgress =
      !!current && !isCreditApplicationTerminal(current.status)
    setHasActiveCreditApp(isCurrentInProgress)
    setCreditAppId(current?.id ?? null)

    // "Primera solicitud" para la pantalla "Revisa los detalles": la que
    // está por crearse es la primera solo si el usuario no tiene NINGUNA
    // credit application previa. Distinto de authService.isFirstCreditApplication
    // (usada abajo), que considera "primera" incluso cuando ya existe 1 —
    // esa responde "¿la credit app en curso es la única que ha tenido?",
    // no "¿la que voy a crear ahora sería la primera?".
    setIsFirstRequest(creditApps.length === 0)

    // La tarjeta "Continuar" (comportamiento híbrido de progressUrl) ya no
    // se limita a la 1ra solicitud del usuario — a partir de la 2da, el
    // flujo abreviado también guarda un progressUrl real que vale la pena
    // poder retomar (ver authService.updateCreditApplicationProgress).
    if (isCurrentInProgress) {
      setInProgressCotizacionId(current?.cotizacion?.id ?? null)
      setInProgressUrl(current?.progressURL ?? null)
    } else {
      setInProgressCotizacionId(null)
      setInProgressUrl(null)
    }

    return { creditApps, current }
  }

  // Retoma el progressUrl guardado de la solicitud en curso (tarjeta
  // "Continuar" en Mis cotizaciones).
  const handleResume = async () => {
    if (inProgressUrl) {
      navigate(inProgressUrl)
      return
    }
    // Fallback por si el valor local no llegó a cargarse: se le vuelve a
    // preguntar al backend.
    const progressURL = await authService.getCreditApplicationProgress()
    if (progressURL) navigate(progressURL)
  }

  // Refrescar al tener éxito, cerrando el modal
  const handleGuarantorSuccess = async () => {
    if (onRefresh) onRefresh()

    // Opcionalmente podemos recargar la info del creditApp
    try {
      await loadCreditAppsState()
    } catch (e) {
      console.error(e)
    }
  }

  // Determinar si el usuario es aval
  const user = authService.getUser()
  const isAval =
    !!user?.cotizacionId || (user?.roles && user.roles.includes('aval'))

  const { triggerFlowSelection } = useFlowSelectionLogic()

  // Cargar creditAppId al montar el componente
  useEffect(() => {
    const loadCreditAppInfo = async () => {
      try {
        setIsLoadingCreditApp(true)
        const { current } = await loadCreditAppsState()

        if (current) {
          console.log('📝 Current credit application info:', current)

          // Mostrar modal automáticamente si requiere aval
          if (
            current.guarantorEnabled === true &&
            current.guarantorUserId === null
          ) {
            console.log('aval required from creditApp', current)
            setIsGuarantorModalOpen(true)
          }
        }
      } catch (error) {
        console.error('Error loading credit app info:', error)
        // Mantiene el mismo comportamiento que ya existía antes de este
        // cambio: ante un error de red se resetea a "sin credit app",
        // en vez de dejar algún estado parcial/previo colgado.
        setCreditAppId(null)
        setUsedCotizacionIds(new Set())
        setHasActiveCreditApp(false)
      } finally {
        setIsLoadingCreditApp(false)
      }
    }

    loadCreditAppInfo()
  }, [])

  // Cada cambio de pestaña vuelve a pedir los datos de esa pestaña, para
  // que la información no se quede desactualizada al ir y venir entre
  // Activas y Vencidas.
  useEffect(() => {
    if (isFirstTabEffect.current) {
      isFirstTabEffect.current = false
      return
    }

    if (activeTab === 'vencidas') {
      if (!onLoadVencidas) return

      const load = async () => {
        setIsLoadingVencidas(true)
        try {
          setVencidasProposals(await onLoadVencidas())
        } catch (e) {
          console.error('Error cargando cotizaciones vencidas:', e)
          setVencidasProposals([])
        } finally {
          setIsLoadingVencidas(false)
        }
      }

      load()
    } else {
      onRefreshActivas?.()
    }
  }, [activeTab, onLoadVencidas, onRefreshActivas])

  // Crea la credit application para la cotización de `proposalId` y arranca
  // el flujo correspondiente. Antes requería seleccionar una tarjeta y
  // confirmar con un botón global "Continuar"; ahora cada tarjeta dispara
  // esto directamente desde su propio botón "Solicitar".
  const handleRequest = async (proposalId: string) => {
    if (isProcessingRef.current) {
      console.log('🚫 Ya hay una solicitud en proceso, ignorando...')
      return
    }

    if (usedCotizacionIds.has(proposalId)) {
      console.warn(
        '🚫 Cotización ya usada en una solicitud previa, no se puede solicitar:',
        proposalId
      )
      return
    }

    isProcessingRef.current = true
    setIsSubmittingRequest(true)
    setRequestingProposalId(proposalId)

    try {
      const userDataRaw = localStorage.getItem('user_data')
      let flowType = 'persona_moral'
      let autoSendInvitation = false
      let linkedCotizacionID = null
      let isGuarantorUser = false

      if (userDataRaw) {
        try {
          const userData = JSON.parse(userDataRaw)
          autoSendInvitation = !!userData.autoSendInvitation
          linkedCotizacionID = userData.cotizacionId
          isGuarantorUser =
            Array.isArray(userData.roles) && userData.roles.includes('aval')
        } catch (e) {
          console.warn('No se pudo parsear user_data:', e)
        }
      }

      if (autoSendInvitation || isGuarantorUser || linkedCotizacionID) {
        let userPersonType = authService.getUserPersonType()
        if (!userPersonType) {
          const user = authService.getUser()
          if (user?.personType) userPersonType = user.personType
        }

        if (userPersonType === 'PM') flowType = 'persona_moral'
        else if (userPersonType === 'PFAE' || userPersonType === 'PF')
          flowType = 'persona_fisica'
        else flowType = 'persona_fisica'
      } else {
        let userPersonType = authService.getUserPersonType()
        if (!userPersonType) {
          const user = authService.getUser()
          if (user?.personType) userPersonType = user.personType
        }

        if (userPersonType === 'PM') flowType = 'persona_moral'
        else if (userPersonType === 'PFAE' || userPersonType === 'PF')
          flowType = 'persona_fisica'
      }

      const payload = {
        cotizacionId: proposalId,
        progressURL: window.location.pathname,
      }

      const token = authService.getToken()
      const headers = {
        'Content-Type': 'application/json',
      } as Record<string, string>
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await apiClient.post('/credit-application', payload, {
        headers,
      })

      if (!response.status) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      await triggerFlowSelection(flowType as any, { isFirstRequest })
    } catch (error) {
      console.error('❌ Error creating credit application:', error)
      // Opcionalmente podemos recargar la info del creditApp
      try {
        await loadCreditAppsState()
      } catch (e) {
        console.error(e)
      }
    } finally {
      isProcessingRef.current = false
      setIsSubmittingRequest(false)
      setRequestingProposalId(null)
    }
  }

  // Una tarjeta puede "Solicitar" solo si: no es aval (los avales no pasan
  // por esta pantalla), no fue usada antes, no hay otra solicitud activa
  // bloqueando una nueva, ya terminó de cargar el estado inicial, y no hay
  // otra solicitud en curso ahora mismo (evita disparar una segunda
  // credit application mientras la primera sigue procesando).
  const canRequest = (proposalId: string) =>
    !isAval &&
    !isLoadingCreditApp &&
    !hasActiveCreditApp &&
    !usedCotizacionIds.has(proposalId) &&
    !isSubmittingRequest

  return (
    <>
      <GuarantorInviteModal
        isOpen={isGuarantorModalOpen}
        onClose={() => setIsGuarantorModalOpen(false)}
        onSuccess={handleGuarantorSuccess}
        creditApplicationId={creditAppId || undefined}
      />
      {/* Mobile Layout */}
      <div className="min-h-screen flex flex-col md:hidden">
        {/* Mobile Header */}
        <Navbar logoSize="large" />

        {/* Mobile Content */}
        <div className="flex-1 bg-olr-white p-4 sm:p-6">
          <div className="max-w-md mx-auto">
            {reviewingProposal ? (
              <RequestLeaseReviewContent
                proposal={reviewingProposal}
                onBack={() => setReviewingProposal(null)}
                onConfirm={() => handleRequest(reviewingProposal.id)}
                isSubmitting={isSubmittingRequest}
              />
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-3 text-center">
                  Tus cotizaciones pendientes
                </h2>

                <ProposalTabs active={activeTab} onChange={setActiveTab} />

                {activeTab === 'activas' ? (
                  <>
                    <p className="text-sm text-gray-700 mb-6 text-center leading-relaxed">
                      Te mostramos las cotizaciones que solicitaste y que has
                      mostrado interés.
                    </p>

                    <div className="space-y-3 mb-6">
                      {proposals.map(proposal => (
                        <PendingProposalItem
                          key={proposal.id}
                          id={proposal.id}
                          marca={proposal.vehicleData.marca}
                          modelo={proposal.vehicleData.modelo}
                          valorFactura={proposal.vehicleData.valorFactura}
                          status={proposal.status}
                          created_at={proposal.created_at}
                          disabled={usedCotizacionIds.has(proposal.id)}
                          inProgress={
                            inProgressCotizacionId === proposal.id &&
                            !isProgressUrlHere
                          }
                          onResume={handleResume}
                          onRequest={
                            canRequest(proposal.id)
                              ? () =>
                                  isFirstRequest
                                    ? handleRequest(proposal.id)
                                    : setReviewingProposal(proposal)
                              : undefined
                          }
                          isRequesting={requestingProposalId === proposal.id}
                          startTime={proposal.startTime}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <VencidasList
                    loading={isLoadingVencidas}
                    proposals={vencidasProposals}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="hidden md:block">
        <AccountLayout>
          {reviewingProposal ? (
            <RequestLeaseReviewContent
              proposal={reviewingProposal}
              onBack={() => setReviewingProposal(null)}
              onConfirm={() => handleRequest(reviewingProposal.id)}
              isSubmitting={isSubmittingRequest}
            />
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Mis cotizaciones
              </h2>

              <ProposalTabs active={activeTab} onChange={setActiveTab} />

              {activeTab === 'activas' ? (
                <>
                  <p className="text-gray-700 mb-6 max-w-2xl">
                    Te mostramos las cotizaciones que solicitaste y que has
                    mostrado interés.
                  </p>

                  <div className="w-full flex flex-col">
                    {proposals.map(proposal => (
                      <PendingProposalItem
                        key={proposal.id}
                        id={proposal.id}
                        marca={proposal.vehicleData.marca}
                        modelo={proposal.vehicleData.modelo}
                        valorFactura={proposal.vehicleData.valorFactura}
                        status={proposal.status}
                        created_at={proposal.created_at}
                        disabled={usedCotizacionIds.has(proposal.id)}
                        inProgress={
                          inProgressCotizacionId === proposal.id &&
                          !isProgressUrlHere
                        }
                        onResume={handleResume}
                        onRequest={
                          canRequest(proposal.id)
                            ? () =>
                                isFirstRequest
                                  ? handleRequest(proposal.id)
                                  : setReviewingProposal(proposal)
                            : undefined
                        }
                        isRequesting={requestingProposalId === proposal.id}
                        startTime={proposal.startTime}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <VencidasList
                  loading={isLoadingVencidas}
                  proposals={vencidasProposals}
                />
              )}
            </>
          )}
        </AccountLayout>
      </div>
    </>
  )
}

// Listado de la tab "Vencidas": mismas tarjetas, sin acción disponible
// (no se les pasa onRequest ni inProgress) — ver plan de decisiones por
// default.
const VencidasList: FC<{
  loading: boolean
  proposals: ProposalQuoteOption[]
}> = ({ loading, proposals }) => {
  if (loading) {
    return (
      <div className="bg-white border border-olr-grey-2 rounded-lg p-12 text-center text-olr-body">
        Cargando cotizaciones vencidas...
      </div>
    )
  }

  if (proposals.length === 0) {
    return (
      <div className="bg-white border border-olr-grey-2 rounded-lg p-12 text-center text-olr-body">
        No tienes cotizaciones vencidas.
      </div>
    )
  }

  return (
    <div className="w-full flex flex-col">
      {proposals.map(proposal => (
        <PendingProposalItem
          key={proposal.id}
          id={proposal.id}
          marca={proposal.vehicleData.marca}
          modelo={proposal.vehicleData.modelo}
          valorFactura={proposal.vehicleData.valorFactura}
          status={proposal.status}
          created_at={proposal.created_at}
          startTime={proposal.startTime}
        />
      ))}
    </div>
  )
}

export default PendingProposalsContent
